import React, {useEffect, useId, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {CrmAlert, CrmStep, NextAction, TeamMember} from '../../domain/entities/Pipeline.ts'
import {ConversionAlert, OnHoldFlag} from '../../domain/entities/ConversionPipeline.ts'
import {initialsOf} from '../../utils/initials.ts'
import {TONE, useDateFormatters} from './pipelineView.ts'

export const Avatar: React.FC<{member?: TeamMember | null; name?: string | null; size?: number}> = ({member, name, size = 18}) => {
    const label = member?.name ?? name ?? ''
    const style = {width: size, height: size, fontSize: Math.max(8, Math.round(size * 0.45))}
    if (member?.avatarUrl) {
        return <img src={member.avatarUrl} alt="" aria-hidden="true" style={style} className="rounded-full object-cover flex-none"/>
    }
    return (
        <span aria-hidden="true" style={style}
              className="rounded-full flex-none bg-[#E3EEEF] text-[#14343B] font-bold inline-flex items-center justify-center">
            {member?.initials || initialsOf(label)}
        </span>
    )
}

const CHIP = 'inline-flex items-center gap-[5px] h-[22px] pl-0.5 pr-2 rounded-full bg-neutralBlack-100 flex-none'

export const MemberChip: React.FC<{member?: TeamMember | null; fallbackName?: string | null}> = ({member, fallbackName}) => (
    <span className={CHIP}>
        <Avatar member={member} name={fallbackName}/>
        <span className="text-[11.5px] font-medium text-neutralBlack-900 whitespace-nowrap">{member?.name ?? fallbackName}</span>
    </span>
)

export const UnassignedChip: React.FC = () => {
    const {t} = useTranslation('dashboard')
    return (
        <span className={CHIP}>
            <span aria-hidden="true" className="w-[18px] h-[18px] rounded-full border-[1.5px] border-dashed border-neutralBlack-500 box-border"/>
            <span className="text-[11.5px] font-medium text-neutralBlack-700 whitespace-nowrap">{t('pipelines.unassigned')}</span>
        </span>
    )
}

export const AutoIcon: React.FC<{className?: string}> = ({className = 'w-[14px] h-[14px]'}) => (
    <svg aria-hidden="true" className={className} viewBox="0 0 24 24" fill="#FBBF24" stroke="#B07D00" strokeWidth="1.6" strokeLinejoin="round">
        <path d="M13 2 4 14h7l-1 8 9-12h-7z"/>
    </svg>
)

export const AutoChip: React.FC = () => {
    const {t} = useTranslation('dashboard')
    return (
        <span className={CHIP}>
            <span aria-hidden="true" className="w-[18px] h-[18px] rounded-full bg-[#14343B] inline-flex items-center justify-center">
                <AutoIcon className="w-[11px] h-[11px]"/>
            </span>
            <span className="text-[11.5px] font-medium text-neutralBlack-900 whitespace-nowrap">{t('pipelines.automatic')}</span>
        </span>
    )
}

// Who acts on the next action: a person, nobody yet, or the system.
// `systemWhenWaiting`: the stage has nothing for the staff to do (Conversion,
// no CTAs), so a WAIT shows the system bolt too.
export const NextActionWho: React.FC<{nextAction: NextAction; team: TeamMember[]; systemWhenWaiting?: boolean}> = ({nextAction, team, systemWhenWaiting = false}) => {
    if (nextAction.type === 'PERSON') {
        const member = team.find((m) => m.id === nextAction.assigneeId)
        return <MemberChip member={member} fallbackName={member ? null : nextAction.assigneeId}/>
    }
    if (nextAction.type === 'UNASSIGNED') return <UnassignedChip/>
    if (nextAction.type === 'AUTO' || (nextAction.type === 'WAIT' && systemWhenWaiting)) return <AutoChip/>
    return null
}

export const MarketChip: React.FC<{market: string}> = ({market}) => (
    <span className="inline-flex items-center h-5 px-[7px] rounded-md border border-neutralBlack-200 text-[11px] font-semibold tracking-[.02em] text-neutralBlack-700">
        {market}
    </span>
)

export const Tag: React.FC<{children: React.ReactNode; tone?: keyof typeof TONE; title?: string}> = ({children, tone = 'grey', title}) => (
    <span title={title} className={`inline-flex items-center h-5 px-2 rounded-md text-[11px] font-semibold whitespace-nowrap ${TONE[tone]}`}>
        {children}
    </span>
)

// Destination countries as codes: the design's CSS flags are decorative and
// not in the app; the code is also what screen readers get.
export const Countries: React.FC<{countries: string[]}> = ({countries}) => {
    const {t} = useTranslation('countries')
    if (!countries?.length) return null
    const shown = countries.slice(0, 3)
    const names = countries.map((code) => t(code, {defaultValue: code})).join(', ')
    return (
        <span className="inline-flex items-center gap-1" title={names}>
            <span className="sr-only">{names}</span>
            {shown.map((code) => (
                <span key={code} aria-hidden="true"
                      className="inline-flex items-center h-[18px] px-1 rounded bg-neutralBlack-100 text-[10.5px] font-bold text-neutralBlack-700 tracking-[.03em]">
                    {code}
                </span>
            ))}
            {countries.length > 3 && <span aria-hidden="true" className="text-[12px] font-semibold text-neutralBlack-600">+{countries.length - 3}</span>}
        </span>
    )
}

// Contact failed and Paused until; plus the derived alerts the card shows.
export const CrmBadges: React.FC<{
    contactFailed: boolean
    contactFailedReason?: string | null
    pausedUntil?: number | null
    alerts?: CrmAlert[]
    messagesSent?: number
}> = ({contactFailed, contactFailedReason, pausedUntil, alerts = [], messagesSent}) => {
    const {t} = useTranslation('dashboard')
    const {formatDay} = useDateFormatters()
    const paused = pausedUntil != null && pausedUntil > Date.now()
    const extra = alerts.filter((a) => a === 'LATE_ASSIGNMENT' || a === 'NO_REPLY')
    if (!contactFailed && !paused && extra.length === 0) return null
    return (
        <span className="flex flex-wrap items-center gap-1.5">
            {contactFailed && (
                <Tag tone="red" title={contactFailedReason ?? t('pipelines.badge.contactFailedHelp')}>{t('pipelines.badge.contactFailed')}</Tag>
            )}
            {contactFailed && contactFailedReason && (
                <span className="text-[11.5px] text-[#B42318]">{contactFailedReason}</span>
            )}
            {paused && <Tag tone="amber" title={t('pipelines.badge.pausedHelp')}>{t('pipelines.badge.pausedUntil', {date: formatDay(pausedUntil)})}</Tag>}
            {extra.map((alert) => (
                <Tag key={alert} tone={alert === 'NO_REPLY' && (messagesSent ?? 0) < 3 ? 'grey' : 'amber'}>
                    {t(`pipelines.alert.${alert}`, {count: messagesSent ?? 0})}
                </Tag>
            ))}
        </span>
    )
}

// "Touchpoint 2 of 4" with one dot per planned touch.
export const StepDots: React.FC<{step: CrmStep}> = ({step}) => {
    const {t} = useTranslation('dashboard')
    const label = t('pipelines.step', {index: step.index, total: step.total})
    return (
        <span className="flex items-center gap-2">
            <span className="text-[11.5px] text-neutralBlack-600 whitespace-nowrap">{label}</span>
            <span aria-hidden="true" className="inline-flex items-center gap-1">
                {Array.from({length: step.total}, (_, i) => (
                    <span key={i}
                          className={`w-[7px] h-[7px] rounded-full box-border flex-none ${i < step.index - 1 ? 'bg-[#14343B]' : i === step.index - 1 ? 'bg-white border-[1.5px] border-[#14343B]' : 'bg-white border-[1.5px] border-neutralBlack-400'}`}/>
                ))}
            </span>
        </span>
    )
}

export const ChannelIcon: React.FC<{channel?: string | null}> = ({channel}) => {
    const {t} = useTranslation('dashboard')
    if (!channel) return null
    const label = t(`pipelines.channel.${channel}`, {defaultValue: channel})
    return (
        <span title={label} className="inline-flex items-center h-5 px-1.5 rounded-md border border-neutralBlack-200 text-neutralBlack-700">
            <span className="sr-only">{label}</span>
            {channel === 'EMAIL' ? (
                <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>
            ) : channel === 'PHONE' ? (
                <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z"/></svg>
            ) : (
                <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a8 8 0 0 1-11.8 7L4 20l1.1-4.6A8 8 0 1 1 21 12z"/></svg>
            )}
        </span>
    )
}

export const Spinner: React.FC = () => (
    <svg aria-hidden="true" className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <path d="M21 12a9 9 0 1 1-6.2-8.55"/>
    </svg>
)

// Copy-to-clipboard button that says "Copied" for 2 s.
export const CopyButton: React.FC<{text: string | null | undefined; label: string; className?: string; children?: React.ReactNode}> = ({text, label, className, children}) => {
    const {t} = useTranslation('dashboard')
    const [copied, setCopied] = React.useState(false)
    if (!text) return null
    const copy = () => {
        navigator.clipboard?.writeText(text).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }).catch(() => undefined)
    }
    return (
        <button type="button" onClick={copy} aria-label={label} title={label}
                className={className ?? 'h-7 px-2 rounded-md text-[12px] font-medium text-neutralBlack-700 hover:bg-neutralBlack-100 transition-colors duration-200 ease-out'}>
            <span aria-live="polite">{copied ? t('pipelines.copied') : (children ?? t('pipelines.copy'))}</span>
        </button>
    )
}

// Conversion alerts the card and panel show as badges (contract §1.2).
// ON_HOLD and AGENCY_REVIEW come from the flags, so they are not repeated.
const CONVERSION_ALERT_TONE: Partial<Record<string, keyof typeof TONE>> = {
    TOO_FEW_PHOTOS: 'amber',
    TOO_MANY_PHOTOS: 'amber',
    MISSING_INFO: 'amber',
    PAYMENT_FAILED: 'red',
    CONTACT_FAILED: 'red',
    NO_TRIP_DATES: 'grey',
    UNVERIFIED: 'grey',
    PRICING_PLACEHOLDER: 'amber',
    PAGES_UNKNOWN: 'amber',
}

export const ConversionBadges: React.FC<{
    onHold: OnHoldFlag | null
    agencyReview: boolean
    alerts?: ConversionAlert[]
    messagesSent?: number
}> = ({onHold, agencyReview, alerts = [], messagesSent}) => {
    const {t} = useTranslation('dashboard')
    const {formatDay} = useDateFormatters()
    const held = onHold != null && onHold.until > Date.now()
    const extra = alerts.filter((a) => a === 'NO_REPLY' || a in CONVERSION_ALERT_TONE)
    if (!held && !agencyReview && extra.length === 0) return null
    return (
        <span className="flex flex-wrap items-center gap-1.5">
            {held && <Tag tone="grey" title={onHold?.note ?? t('pipelines.badge.onHoldHelp')}>{t('pipelines.badge.onHoldUntil', {date: formatDay(onHold!.until)})}</Tag>}
            {agencyReview && <Tag tone="teal" title={t('pipelines.badge.agencyReviewHelp')}>{t('pipelines.badge.agencyReview')}</Tag>}
            {extra.map((alert) => (
                <Tag key={alert} tone={alert === 'NO_REPLY' ? ((messagesSent ?? 0) < 3 ? 'grey' : 'amber') : CONVERSION_ALERT_TONE[alert] ?? 'grey'}
                     title={t(`pipelines.alertHelp.${alert}`, {defaultValue: ''}) || undefined}>
                    {t(`pipelines.alert.${alert}`, {count: messagesSent ?? 0, defaultValue: alert})}
                </Tag>
            ))}
        </span>
    )
}

export type CardMenuItem = {label: string; onSelect: () => void}

// "⋯" menu on a card: the keyboard and screen-reader alternative to dragging,
// plus Override. Arrow keys move, Esc or a click outside closes it.
export const CardMenu: React.FC<{label: string; items: CardMenuItem[]}> = ({label, items}) => {
    const [open, setOpen] = useState(false)
    const root = useRef<HTMLDivElement>(null)
    const button = useRef<HTMLButtonElement>(null)
    const menuId = useId()

    useEffect(() => {
        if (!open) return
        root.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus()
        const onPointer = (e: MouseEvent) => { if (!root.current?.contains(e.target as Node)) setOpen(false) }
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation()
                setOpen(false)
                button.current?.focus()
                return
            }
            if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
            e.preventDefault()
            const entries = Array.from(root.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [])
            const index = entries.indexOf(document.activeElement as HTMLButtonElement)
            const next = e.key === 'ArrowDown' ? (index + 1) % entries.length : (index - 1 + entries.length) % entries.length
            entries[next]?.focus()
        }
        document.addEventListener('mousedown', onPointer)
        document.addEventListener('keydown', onKey, true)
        return () => {
            document.removeEventListener('mousedown', onPointer)
            document.removeEventListener('keydown', onKey, true)
        }
    }, [open])

    if (items.length === 0) return null
    return (
        <div ref={root} className="absolute top-2.5 right-2.5">
            <button ref={button} type="button" aria-label={label} title={label} aria-haspopup="menu" aria-expanded={open}
                    aria-controls={open ? menuId : undefined} onClick={() => setOpen((o) => !o)}
                    className="w-7 h-7 rounded-md flex items-center justify-center text-neutralBlack-600 hover:bg-neutralBlack-100 hover:text-neutralBlack-900 transition-colors duration-200 ease-out">
                <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="5" cy="12" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="19" cy="12" r="1.8"/>
                </svg>
            </button>
            {open && (
                <div id={menuId} role="menu" aria-label={label}
                     className="absolute right-0 top-8 z-30 min-w-[180px] bg-white border border-neutralBlack-200 rounded-xl shadow-lg p-1.5">
                    {items.map((item) => (
                        <button key={item.label} type="button" role="menuitem"
                                onClick={() => { setOpen(false); item.onSelect() }}
                                className="w-full text-left px-2.5 py-2 rounded-lg text-[13px] text-neutralBlack-900 hover:bg-neutralBlack-100 focus:bg-neutralBlack-100 focus:outline-none transition-colors duration-200 ease-out">
                            {item.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}
